import {
  IconArrowBarToLeft,
  IconArrowBarToRight,
  IconClearAll,
  IconDotsVertical,
  IconEyeOff,
  IconFilter,
  IconFilterOff,
  IconPinnedOff,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react"
import { Column } from "@tanstack/react-table"
import { match } from "ts-pattern"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../DropdownMenu"
import { KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID } from "../DataTable"
import { BooleanFilter } from "../filters/BooleanFilter"
import { DateRangeFilter } from "../filters/DateRangeFilter"
import { MultiEnumFilter } from "../filters/MultiEnumFilter"
import { NumberRangeFilter } from "../filters/NumberRangeFilter"
import { SingleEnumFilter } from "../filters/SingleEnumFilter"
import { LE_TableInstance } from "../types"

export function shouldShowDropdown<T extends Record<string, any> = object>(
  col: Column<T>,
) {
  const canSort = col.getCanSort()
  const canFilter =
    (col.getCanFilter() && !!col.columnDef.filterFn) ||
    // Force show filter in the kebab menu, for filterin in e.g. display columns
    col.columnDef.enableColumnFilter === true
  const canPin = col.getCanPin()
  const hasChildren = col.getFlatColumns().length > 1
  const canHide = col.getCanHide()
  if (col.columnDef.meta?.hideHeader) return false

  return (canFilter || canSort || canPin || canHide) && !hasChildren
}

export function ColumnDropdown<T extends Record<string, any> = object>({
  col,
  table,
}: {
  col: Column<T>
  table: LE_TableInstance<T>
}) {
  const state = table.getState()
  const selectedFilterValue = state.columnFilters?.find((f) => f.id === col.id)?.value

  if ([KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID].includes(col.id)) return null

  const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
  if (typeof label !== "string") {
    throw new Error(
      "column `header` must be a string, or `meta.textLabel` must be set.",
    )
  }

  const canSort = col.getCanSort()
  const canFilter =
    (col.getCanFilter() && !!col.columnDef.filterFn) ||
    // Force show filter in the kebab menu, for filterin in e.g. display columns
    col.columnDef.enableColumnFilter === true
  const canPin = col.getCanPin()
  const canHide = col.getCanHide()

  if (col.columnDef.meta?.hideHeader) return null

  const shouldShow = shouldShowDropdown(col)

  return shouldShow ? (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex">
        <span className="cursor-pointer">
          <IconDotsVertical size={14} className="" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-2">
        {canSort && (
          <>
            <DropdownMenuItem
              disabled={!col.getIsSorted()}
              onClick={() => col.clearSorting()}
            >
              <IconClearAll /> Clear sort
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={col.getIsSorted() === "asc"}
              onClick={() => col.toggleSorting(false)}
            >
              <IconSortAscending />
              Sort by {label} ascending
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={col.getIsSorted() === "desc"}
              onClick={() => col.toggleSorting(true)}
            >
              <IconSortDescending />
              Sort by {label} descending
            </DropdownMenuItem>
          </>
        )}

        {canFilter && (
          <>
            {canSort ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              disabled={!selectedFilterValue}
              onClick={() => col.setFilterValue(undefined)}
            >
              <IconFilterOff /> Clear filter
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <IconFilter /> Filter
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {match(col.columnDef.filterFn)
                  .with("multiEnum", () => (
                    <MultiEnumFilter
                      col={col}
                      filterValue={selectedFilterValue as string[]}
                      table={table}
                    />
                  ))
                  .with("singleEnum", () => (
                    <SingleEnumFilter
                      col={col}
                      filterValue={selectedFilterValue as string}
                      table={table}
                    />
                  ))
                  .with("inNumberRange", () => (
                    <NumberRangeFilter
                      col={col}
                      filterValue={selectedFilterValue as [number, number, boolean]}
                    />
                  ))
                  .with("inDateRange", () => (
                    <DateRangeFilter
                      col={col}
                      filterValue={selectedFilterValue as [string, string, boolean]}
                    />
                  ))
                  .with("boolean", () => (
                    <BooleanFilter
                      col={col}
                      filterValue={selectedFilterValue as boolean}
                    />
                  ))
                  .otherwise(() => null)}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
        {canPin ? (
          <>
            {canFilter || canSort ? <DropdownMenuSeparator /> : null}
            {col.getIsPinned() ? (
              <DropdownMenuItem onClick={() => col.pin(false)}>
                <IconPinnedOff />
                Unpin column
              </DropdownMenuItem>
            ) : null}

            {col.getIsPinned() !== "left" ? (
              <DropdownMenuItem onClick={() => col.pin("left")}>
                <IconArrowBarToLeft />
                Pin column left
              </DropdownMenuItem>
            ) : null}
            {col.getIsPinned() !== "right" ? (
              <DropdownMenuItem
                onClick={() => {
                  col.pin("right")

                  table.setColumnPinning((state) => ({
                    ...state,
                    right: [
                      ...(state.right ?? []).filter(
                        (id) => id !== KEBAB_MENU_COLUMN_ID,
                      ),
                      KEBAB_MENU_COLUMN_ID,
                    ],
                  }))
                }}
              >
                <IconArrowBarToRight />
                Pin column right
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}

        {canHide ? (
          <>
            {canPin || canFilter || canSort ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              onClick={() => {
                table.setColumnVisibility((state) => ({
                  ...state,
                  [col.id]: false,
                }))
              }}
            >
              <IconEyeOff />
              Hide column
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null
}
